// src/pages/benefits/ReccMemberDiscounts.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#c4d8ec";
const sectionHeaderBorder = "#4e81c4";
const linkBlue = "#005695";

function DiscountCard({ title, logoAlt, logoSrc, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid #dde4f0",
        p: 3,
        mb: 3,
        backgroundColor: "#ffffff",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2.5}
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
      >
        <Box
          sx={{
            width: 120,
            height: 60,
            borderRadius: 1.5,
            border: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt={logoAlt}
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              {logoAlt}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#111827" }}
          >
            {title}
          </Typography>
          {children}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ReccMemberDiscounts() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; RECC Member Benefits &gt; Member Discounts
        </Typography>

        {/* Page title + intro */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          RECC Member Discounts
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563", maxWidth: 900 }}>
          As a RECC member, you can access exclusive discounts and offers from
          trusted partners. These benefits are designed to help you build
          credibility, manage risk and reach more consumers.
        </Typography>

        {/* Which? Trusted Traders */}
        <DiscountCard
          title="Which? Trusted Traders"
          logoAlt="Which? Trusted Traders logo"
          logoSrc="/assets/logos/which-trusted-traders.png" // update path
        >
          <Typography variant="body2" sx={{ mb: 1.5, color: "#4b5563" }}>
            Run by Which?, the UK’s most trusted consumer champion. Their
            endorsement logo helps businesses stand out in competitive markets.
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1, mb: 0.5, color: "#111827" }}
          >
            RECC member offer
          </Typography>
          <List dense sx={{ pl: 0 }}>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="50% off first 6 months of membership."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Full refund on application fee (if accepted within 30 days)."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
          </List>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1.5, mb: 0.5, color: "#111827" }}
          >
            Why join?
          </Typography>
          <List dense sx={{ pl: 0 }}>
            {[
              "Increase your business credibility and recognition.",
              "Join a widely recognised, trust-driven brand.",
              "Combine RECC + Which? logos.",
              "Reach more safety-conscious consumers.",
            ].map((text) => (
              <ListItem key={text} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ArrowRightIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{ variant: "body2", color: "#111827" }}
                />
              </ListItem>
            ))}
          </List>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1.5, mb: 0.5, color: "#111827" }}
          >
            How to apply
          </Typography>
          <List dense sx={{ pl: 0 }}>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Visit the{" "}
                    <Box
                      component="a"
                      href="https://trustedtraders.which.co.uk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: linkBlue,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Which? Trusted Traders
                    </Box>{" "}
                    website and follow the instructions on how to join.
                  </>
                }
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Quote your RECC membership number."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
          </List>
        </DiscountCard>

        {/* Buy With Confidence */}
        <DiscountCard
          title="Buy With Confidence"
          logoAlt="Buy With Confidence logo"
          logoSrc="/assets/logos/buy-with-confidence.png" // update path
        >
          <Typography variant="body2" sx={{ mb: 1.5, color: "#4b5563" }}>
            Run by Trading Standards, Buy With Confidence (BWC) is the UK’s largest
            local authority-backed trader approval scheme. Their approval logo helps
            businesses demonstrate reliability and build consumer trust.
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1, mb: 0.5, color: "#111827" }}
          >
            RECC member offer
          </Typography>
          <List dense sx={{ pl: 0 }}>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="25% off the application fee."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="25% off the first year’s membership."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
          </List>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1.5, mb: 0.5, color: "#111827" }}
          >
            Why join?
          </Typography>
          <List dense sx={{ pl: 0 }}>
            {[
              "Gain recognition as a trusted, vetted business.",
              "Supported and promoted by Trading Standards and over 50 local authorities.",
              "Combine RECC + BWC logos to give consumers double assurance.",
              "Build consumer confidence, especially for high-value small-scale energy installations.",
            ].map((text) => (
              <ListItem key={text} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ArrowRightIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{ variant: "body2", color: "#111827" }}
                />
              </ListItem>
            ))}
          </List>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1.5, mb: 0.5, color: "#111827" }}
          >
            How to apply
          </Typography>
          <List dense sx={{ pl: 0 }}>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Click to apply – visit the{" "}
                    <Box
                      component="a"
                      href="https://www.buywithconfidence.gov.uk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: linkBlue,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Buy With Confidence
                    </Box>{" "}
                    website and follow the instructions to join.
                  </>
                }
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Quote your RECC membership number to claim your member discount."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
          </List>
        </DiscountCard>

        {/* Marsh Commercial */}
        <DiscountCard
          title="Marsh Commercial"
          logoAlt="Marsh Commercial logo"
          logoSrc="/assets/logos/marsh-commercial.png" // update path
        >
          <Typography variant="body2" sx={{ mb: 1.5, color: "#4b5563" }}>
            Marsh Commercial understands the risks faced by businesses in the renewable
            sector and has partnered with RECC to offer specialist insurance with
            tailored coverage at competitive rates.
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1, mb: 0.5, color: "#111827" }}
          >
            RECC member offer
          </Typography>
          <List dense sx={{ pl: 0 }}>
            {[
              "Competitive premiums and sector expertise.",
              "Flexible coverage, even before renewal.",
              "Free legal helpline included with some policies.",
            ].map((text) => (
              <ListItem key={text} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ArrowRightIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{ variant: "body2", color: "#111827" }}
                />
              </ListItem>
            ))}
          </List>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1.5, mb: 0.5, color: "#111827" }}
          >
            Coverage includes
          </Typography>
          <List dense sx={{ pl: 0 }}>
            {[
              "Public Liability – up to £5M.",
              "Professional Indemnity – from £100K.",
              "Product Efficacy & Financial Loss.",
              "Employers’ Liability.",
              "Legal Expenses + Helpline.",
            ].map((text) => (
              <ListItem key={text} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ArrowRightIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{ variant: "body2", color: "#111827" }}
                />
              </ListItem>
            ))}
          </List>

          <Typography
            variant="subtitle2"
            sx={{ mt: 1.5, mb: 0.5, color: "#111827" }}
          >
            How to access
          </Typography>
          <List dense sx={{ pl: 0, mb: 1 }}>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Phone:{" "}
                    <Box
                      component="a"
                      href="tel:01905892367"
                      sx={{
                        color: linkBlue,
                        textDecoration: "none",
                        fontWeight: 600,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      01905 892 367
                    </Box>
                  </>
                }
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Email:{" "}
                    <Box
                      component="a"
                      href="mailto:RECC@marshcommercial.co.uk"
                      sx={{
                        color: linkBlue,
                        textDecoration: "none",
                        fontWeight: 600,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      RECC@marshcommercial.co.uk
                    </Box>
                  </>
                }
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Quote your RECC membership number."
                primaryTypographyProps={{ variant: "body2", color: "#111827" }}
              />
            </ListItem>
          </List>

          <Typography
            variant="caption"
            sx={{ display: "block", color: "#6b7280", mt: 1.5 }}
          >
            Renewable Energy Consumer Code (RECC), administered by Renewable Energy
            Assurance Ltd (REAL) is an introducer to Marsh Ltd. Marsh Commercial is a
            trading name of Marsh Ltd. Marsh Ltd is authorised and regulated by the
            Financial Conduct Authority for General Insurance Distribution and Credit
            Broking (Firm Reference No. 307511). Copyright © 2022 Marsh Ltd. Registered
            in England and Wales Number: 1507274, Registered office: 1 Tower Place West,
            Tower Place, London EC3R 5BU. All rights reserved.
          </Typography>
        </DiscountCard>

        {/* IET */}
        <DiscountCard
          title="IET"
          logoAlt="IET logo"
          logoSrc="/assets/logos/iet.png" // update path
        >
          <Typography variant="body2" sx={{ mb: 1.5, color: "#4b5563" }}>
            IET Code of Practice for Electrical Energy Storage Systems. Copies are
            available from IET for £60 (£39 for IET members, with a discounted price for
            RECC members).
          </Typography>
          <Typography variant="body2" sx={{ color: "#4b5563" }}>
            Contact RECC for more information about the discount.
          </Typography>
        </DiscountCard>
      </Container>
    </Box>
  );
}
